import { t } from '@/i18n'
import {
  fromRules,
  HHMM,
  hasVisitPack,
  isBounded,
  isRuleType,
  ISO_DATE,
  parsePrice,
  priceText,
  toRules,
  type DraftBlock,
  type GroupBlock,
  type PlanBlock,
  type PlanDraft,
  type PlanRules,
  type RuleBlock,
  type AgeBlock,
  type TimeWindow,
  type ValidityBlock,
} from './pass-rules'

/// What a plan SAYS, in Ukrainian, and what is wrong with a draft — the
/// constructor's live preview and the one-line summary in the price list.
/// Human text is the client's job (specs/passes.md §2a): the server keeps
/// primitives.

const s = () => t.passes.summary

// ---- dates and times in words ----

const shortDate = new Intl.DateTimeFormat('uk-UA', { day: 'numeric', month: 'short' })
const longDate = new Intl.DateTimeFormat('uk-UA', { day: 'numeric', month: 'short', year: 'numeric' })

/// «31 груд.» this year, «31 груд. 2027» otherwise.
export const dateWords = (iso: string, today = new Date()): string => {
  const date = new Date(`${iso}T12:00:00Z`)
  const format = date.getUTCFullYear() === today.getFullYear() ? shortDate : longDate
  return format.format(date).replace(/\s*р\.$/, '')
}

/// «07:00» → «7:00».
export const timeWords = (hhmm: string): string => hhmm.replace(/^0(\d)/, '$1')

/// Monday first — the week a Ukrainian club lives in.
export const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0] as const

export const sameDays = (a: readonly number[], b: readonly number[]): boolean =>
  a.length === b.length && a.every((day) => b.includes(day))

/// «будні», «вихідні», «щодня», «пн, ср, пт», «пн–чт».
export const daysWords = (days: readonly number[]): string => {
  if (days.length === 0 || days.length === 7) return s().everyDay
  if (sameDays(days, [1, 2, 3, 4, 5])) return s().workdays
  if (sameDays(days, [6, 0])) return s().weekend
  const ordered = WEEK_ORDER.filter((day) => days.includes(day))
  const positions = ordered.map((day) => WEEK_ORDER.indexOf(day))
  const contiguous = positions.every((position, i) => i === 0 || position === (positions[i - 1] ?? -2) + 1)
  if (contiguous && ordered.length >= 3) {
    return `${s().shortDays[ordered[0] ?? 1]}–${s().shortDays[ordered[ordered.length - 1] ?? 0]}`
  }
  return ordered.map((day) => s().shortDays[day]).join(', ')
}

export const isWholeDay = (window: TimeWindow): boolean => window.from === '00:00' && window.to === '23:59'

/// «будні 7:00–16:00», «вихідні весь день», «будні 22:00–6:00 наступного ранку».
export const windowWords = (window: TimeWindow): string => {
  const days = daysWords(window.days)
  if (isWholeDay(window)) return window.days.length === 0 ? s().allDay : `${days} ${s().allDay}`
  // «з 21:00» till closing, «до 12:00» from opening — how a club says it.
  const hours =
    window.to < window.from
      ? s().night(timeWords(window.from), timeWords(window.to))
      : window.to === '23:59'
        ? s().fromTime(timeWords(window.from))
        : window.from === '00:00'
          ? s().untilTime(timeWords(window.to))
          : `${timeWords(window.from)}–${timeWords(window.to)}`
  return window.days.length === 0 ? hours : `${days} ${hours}`
}

// ---- the summary ----

const find = <T extends PlanBlock['type']>(blocks: readonly PlanBlock[], type: T) =>
  blocks.find((block) => block.type === type) as Extract<PlanBlock, { type: T }> | undefined

const ruleParts = (blocks: readonly RuleBlock[], packFallback: string | null): string[] => {
  const visits = find(blocks, 'visits')
  const hours = find(blocks, 'hours')
  const zones = find(blocks, 'zones')
  const limits = find(blocks, 'limits')
  return [
    visits ? s().visits(visits.count) : packFallback,
    hours && hours.windows.length > 0 ? hours.windows.map(windowWords).join(' + ') : null,
    zones && zones.zones.length > 0 ? zones.zones.join(', ') : null,
    limits?.perDay != null ? s().perDay(limits.perDay) : null,
    limits?.perWeek != null ? s().perWeek(limits.perWeek) : null,
    limits?.perMonth != null ? s().perMonth(limits.perMonth) : null,
    find(blocks, 'trainer') ? s().withTrainer : null,
  ].filter((part): part is string => Boolean(part))
}

/// A group's rules in words — without a zone that only repeats the group's
/// name: «Басейн: 8 візитів», not «Басейн: 8 візитів, Басейн».
const groupParts = (group: GroupBlock): string[] => {
  const label = group.label.trim().toLocaleLowerCase('uk')
  const zones = find(group.blocks, 'zones')
  const blocks =
    zones && zones.zones.length === 1 && zones.zones[0]?.toLocaleLowerCase('uk') === label
      ? group.blocks.filter((block) => block.type !== 'zones')
      : group.blocks
  return ruleParts(blocks, s().unlimited)
}

/// «Басейн: 8 візитів, будні 7:00–16:00».
export const groupWords = (group: GroupBlock): string =>
  `${group.label.trim() || t.passes.blocks['group']?.title || ''}: ${groupParts(group).join(', ')}`

/// A group's rules without its name — the second line of its header.
export const groupRulesLine = (group: GroupBlock): string => groupParts(group).join(', ')

/// «30 днів», «3 місяці», «1 місяць, не пізніше 31 груд.», «до 31 груд.».
export const termWords = (validity: ValidityBlock | undefined): string | null => {
  const until = validity?.notAfter ? dateWords(validity.notAfter) : null
  if (validity?.months != null) return until ? s().monthsUntil(validity.months, until) : s().months(validity.months)
  if (validity?.days != null) return until ? s().daysUntil(validity.days, until) : s().days(validity.days)
  return until ? s().notAfter(until) : null
}

/// The plan in a few phrases, most important first: how long, how many, when,
/// where, how often, then the groups, then the extras.
export const summaryParts = (rules: PlanRules): readonly string[] => {
  const blocks = rules.blocks
  const shared = blocks.filter((block): block is RuleBlock => isRuleType(block.type))
  const groups = blocks.filter((block): block is GroupBlock => block.type === 'group')
  const validity = find(blocks, 'validity')
  const start = find(blocks, 'start')
  const freeze = find(blocks, 'freeze')
  const stay = find(blocks, 'stay')
  const guests = find(blocks, 'guests')
  const eligibility = find(blocks, 'eligibility')
  const discounts = find(blocks, 'discounts')
  const age = find(blocks, 'age')
  const included = find(blocks, 'included')
  const requiresPass = find(blocks, 'requiresPass')
  const classVisit = find(blocks, 'classVisit')

  const term = termWords(validity)
  const starts =
    start?.rule === 'on_first_visit'
      ? s().fromFirstVisit
      : start?.rule === 'fixed_date'
        ? start.date
          ? s().fromDate(dateWords(start.date))
          : s().fromSale
        : start?.rule === 'after_previous'
          ? s().afterPrevious
          : null
  // «безліміт» only where it is news: a time-bounded plan without any pack.
  const unlimited = groups.length === 0 && !shared.some((b) => b.type === 'visits') && term ? s().unlimited : null

  return [
    term,
    starts,
    requiresPass ? s().requiresPass : null,
    ...ruleParts(shared, unlimited),
    ...groups.map(groupWords),
    classVisit ? s().classVisit : null,
    stay ? s().stay(stay.minutes) : null,
    guests ? s().guests(guests.count) : null,
    freeze ? s().freeze(freeze.maxDays) : null,
    included && included.items.length > 0 ? s().included(included.items) : null,
    age && (age.minAge != null || age.maxAge != null) ? ageWords(age) : null,
    eligibility?.newClientsOnly ? s().newOnly : null,
    discounts && !discounts.allowed ? s().noDiscounts : null,
  ].filter((part): part is string => Boolean(part))
}

/// «вік до 14 років включно», «вік від 6 до 14 років включно».
export const ageWords = (age: AgeBlock): string => s().age(age.minAge, age.maxAge)

export const summarize = (rules: PlanRules): string => summaryParts(rules).join(' · ')

/// One block in a few words — the line a collapsed block shows on the canvas.
export const blockLine = (block: PlanBlock): string => {
  if (block.type === 'group') return groupWords(block)
  if (isRuleType(block.type)) {
    return ruleParts([block as RuleBlock], null).join(', ') || (t.passes.blocks[block.type]?.hint ?? '')
  }
  const only = summaryParts({ version: 1, blocks: [block] }).filter((part) => part !== s().unlimited)
  if (block.type === 'start' && block.rule === 'on_purchase') return t.passes.editors.start['on_purchase'] ?? ''
  // An empty «Входить у вартість» says what it is for rather than nothing.
  return only.join(', ') || (t.passes.blocks[block.type]?.hint ?? '')
}

// ---- validation (mirrors services/passes/src/utils/blocks.ts) ----

export type DraftProblems = Readonly<{
  name?: string
  price?: string
  /// By block key: what is wrong with that block.
  blocks: Readonly<Record<string, readonly string[]>>
  /// About the plan as a whole (P2).
  plan: readonly string[]
}>

const e = () => t.passes.editors

const inRange = (value: number | null, min: number, max: number): boolean =>
  value === null || (Number.isInteger(value) && value >= min && value <= max)

const windowProblems = (window: TimeWindow): string[] => [
  ...(!HHMM.test(window.from) || !HHMM.test(window.to) ? [e().badTime] : []),
  ...(window.from === window.to ? [e().sameTime] : []),
]

const ruleProblems = (block: RuleBlock): string[] => {
  switch (block.type) {
    case 'zones':
      return block.zones.filter((z) => z.trim()).length === 0 ? [e().zonesRequired] : []
    case 'hours':
      return block.windows.length === 0 ? [e().noWindows] : [...new Set(block.windows.flatMap(windowProblems))]
    case 'visits':
      return inRange(block.count, 1, 1000) ? [] : [e().outOfRange(1, 1000)]
    case 'limits':
      return block.perDay == null && block.perWeek == null && block.perMonth == null
        ? [e().limitsRequired]
        : !inRange(block.perDay, 1, 20) || !inRange(block.perWeek, 1, 100) || !inRange(block.perMonth, 1, 400)
          ? [e().outOfRange(1, 400)]
          : []
    case 'trainer':
      return []
  }
}

const blockProblems = (block: DraftBlock): string[] => {
  if (block.type === 'group') {
    return block.label.trim() === '' ? [t.passes.builder.groupLabelRequired] : []
  }
  if (isRuleType(block.type)) return ruleProblems(block as RuleBlock)
  switch (block.type) {
    case 'validity':
      return block.days == null && block.months == null && !block.notAfter
        ? [e().validityRequired]
        : [
            ...(inRange(block.days, 1, 3650) ? [] : [e().outOfRange(1, 3650)]),
            ...(inRange(block.months ?? null, 1, 120) ? [] : [e().outOfRange(1, 120)]),
            ...(block.notAfter && !ISO_DATE.test(block.notAfter) ? [e().badDate] : []),
          ]
    case 'start':
      return block.date && !ISO_DATE.test(block.date) ? [e().badDate] : []
    case 'freeze':
      return [
        ...(inRange(block.maxDays, 1, 365) && inRange(block.minDays, 1, 365) && inRange(block.maxCount, 1, 50)
          ? []
          : [e().outOfRange(1, 365)]),
        ...(block.minDays > block.maxDays ? [e().freezeMinOverMax] : []),
      ]
    case 'stay':
      return inRange(block.minutes, 15, 1440) ? [] : [e().outOfRange(15, 1440)]
    case 'guests':
      return inRange(block.count, 1, 50) ? [] : [e().outOfRange(1, 50)]
    case 'eligibility':
    case 'discounts':
    case 'requiresPass':
    case 'classVisit':
      return []
    case 'age':
      return block.minAge == null && block.maxAge == null
        ? [e().ageRequired]
        : [
            ...(inRange(block.minAge, 0, 120) && inRange(block.maxAge, 0, 120) ? [] : [e().outOfRange(0, 120)]),
            ...(block.minAge != null && block.maxAge != null && block.minAge > block.maxAge ? [e().ageMinOverMax] : []),
          ]
    case 'included':
      return block.items.filter((item) => item.trim()).length === 0 ? [e().includedRequired] : []
  }
}

export const validateDraft = (draft: PlanDraft): DraftProblems => {
  const all = draft.blocks.flatMap((block): DraftBlock[] =>
    block.type === 'group' ? [block, ...block.blocks] : [block],
  )
  const blocks = Object.fromEntries(
    all.map((block) => [block.key, blockProblems(block)] as const).filter(([, list]) => list.length > 0),
  )
  const price = parsePrice(draft.price)
  return {
    ...(draft.name.trim() === '' ? { name: t.passes.builder.nameRequired } : {}),
    ...(price === null || price > 100_000_000
      ? { price: t.passes.builder.priceInvalid }
      : // A free plan is never sold in the app (O1) — said here, not by a 400.
        draft.soldOnline && price <= 0
        ? { price: t.sale.plan.onlineOffAfterSave }
        : {}),
    blocks,
    plan: [
      ...(isBounded(toRules(draft.blocks)) ? [] : [t.passes.builder.unbounded]),
      ...(draft.blocks.some((block) => block.type === 'classVisit') && !hasVisitPack(toRules(draft.blocks))
        ? [t.passes.builder.classVisitNeedsPack]
        : []),
    ],
  }
}

export const hasProblems = (problems: DraftProblems): boolean =>
  Boolean(problems.name || problems.price) ||
  Object.keys(problems.blocks).length > 0 ||
  problems.plan.length > 0

/// Only the rules — what the tester needs to be able to ask.
export const rulesAreValid = (problems: DraftProblems): boolean =>
  Object.keys(problems.blocks).length === 0 && problems.plan.length === 0

// ---- plan ↔ draft ----

export type PlanInput = Readonly<{
  name: string
  description: string | null
  priceMinor: number
  currency: string
  branchId: string | null
  rules: PlanRules
  /// Sent only when the draft has it — absent keeps the plan's (O2).
  soldOnline?: boolean
}>

export const draftToInput = (draft: PlanDraft): PlanInput => ({
  name: draft.name.trim(),
  description: draft.description.trim() || null,
  priceMinor: parsePrice(draft.price) ?? 0,
  currency: 'UAH',
  branchId: draft.branchId,
  rules: toRules(draft.blocks),
  ...(draft.soldOnline === undefined ? {} : { soldOnline: draft.soldOnline }),
})

export const draftFromPlan = (
  plan: Readonly<{
    name: string
    description: string | null
    priceMinor: number
    branchId: string | null
    rules: PlanRules
    soldOnline?: boolean
  }>,
  options: Readonly<{ copy: boolean }>,
): PlanDraft => ({
  name: options.copy ? t.passes.plans.copyName(plan.name) : plan.name,
  description: plan.description ?? '',
  price: priceText(plan.priceMinor),
  branchId: plan.branchId,
  blocks: fromRules(plan.rules),
  // A copy is a new plan: it goes on sale in the app when somebody says so.
  ...(options.copy ? {} : plan.soldOnline === undefined ? {} : { soldOnline: plan.soldOnline }),
})

/// Equal for the «є незбережені зміни» note: keys differ between two loads of
/// the same plan, the wire form does not.
export const draftsEqual = (a: PlanDraft, b: PlanDraft): boolean =>
  JSON.stringify(draftToInput(a)) === JSON.stringify(draftToInput(b))

export const emptyDraft = (branchId: string | null): PlanDraft => ({
  name: '',
  description: '',
  price: '',
  branchId,
  blocks: [],
})

// ---- presets ----

export const PRESETS = [
  'unlimitedMonth',
  'twelveVisits',
  'morning',
  'gymPool',
  'weekdaysWeekend',
  'trainerEvening',
  'firstMonth',
  'untilYearEnd',
  'kids',
  'trainerPack',
] as const
export type PresetKey = (typeof PRESETS)[number]

const WORKDAYS = [1, 2, 3, 4, 5]
const WEEKEND = [6, 0]
const win = (days: number[], from: string, to: string): TimeWindow => ({ days, from, to, overnight: to < from })

const keyed = (blocks: readonly PlanBlock[]): readonly DraftBlock[] => fromRules({ version: 1, blocks })

/// A preset's blocks, name and price — a starting point the club edits.
/// `today` decides «до кінця року».
export const presetDraft = (key: PresetKey, branchId: string | null, today = new Date()): PlanDraft => {
  const tp = t.passes.templates
  const month = { type: 'validity', days: 30, notAfter: null } as const
  const freeze = { type: 'freeze', maxDays: 30, minDays: 7, maxCount: 2, paid: false } as const
  const base = (name: string, price: string, blocks: readonly PlanBlock[]): PlanDraft => ({
    name,
    description: '',
    price,
    branchId,
    blocks: keyed(blocks),
  })
  switch (key) {
    case 'unlimitedMonth':
      return base(tp.unlimitedMonth.name, '1200', [month, { type: 'start', rule: 'on_purchase', date: null }, freeze])
    case 'twelveVisits':
      return base(tp.twelveVisits.name, '1500', [
        { type: 'visits', count: 12 },
        { type: 'validity', days: 60, notAfter: null },
        { type: 'start', rule: 'on_first_visit', date: null },
      ])
    case 'morning':
      return base(tp.morning.name, '800', [month, { type: 'hours', windows: [win(WORKDAYS, '07:00', '16:00')] }])
    case 'gymPool':
      return base(tp.gymPool.name, '1900', [
        month,
        { type: 'group', id: 'g1', label: tp.groupGym, blocks: [{ type: 'zones', zones: [tp.zoneGym] }] },
        {
          type: 'group',
          id: 'g2',
          label: tp.groupPool,
          blocks: [
            { type: 'zones', zones: [tp.zonePool] },
            { type: 'visits', count: 8 },
          ],
        },
        freeze,
      ])
    case 'weekdaysWeekend':
      return base(tp.weekdaysWeekend.name, '1000', [
        month,
        { type: 'hours', windows: [win(WORKDAYS, '07:00', '16:00'), win(WEEKEND, '00:00', '23:59')] },
      ])
    case 'trainerEvening':
      return base(tp.trainerEvening.name, '900', [
        month,
        { type: 'limits', perDay: 1, perWeek: 2, perMonth: null },
        {
          type: 'group',
          id: 'g1',
          label: tp.groupDay,
          blocks: [
            { type: 'zones', zones: [tp.zoneGym] },
            { type: 'hours', windows: [win([], '06:00', '21:00')] },
          ],
        },
        {
          type: 'group',
          id: 'g2',
          label: tp.groupLate,
          blocks: [
            { type: 'zones', zones: [tp.zoneGym] },
            { type: 'hours', windows: [win([], '21:00', '23:59')] },
            { type: 'trainer' },
          ],
        },
      ])
    case 'firstMonth':
      return base(tp.firstMonth.name, '600', [
        month,
        { type: 'start', rule: 'on_purchase', date: null },
        { type: 'eligibility', newClientsOnly: true },
      ])
    case 'kids':
      return base(tp.kids.name, '700', [
        { type: 'validity', days: null, months: 1, notAfter: null },
        { type: 'start', rule: 'on_purchase', date: null },
        { type: 'age', minAge: null, maxAge: 14 },
      ])
    case 'trainerPack':
      // A trainer package without gym entry: the client also needs a gym pass.
      return base(tp.trainerPack.name, '2400', [
        { type: 'visits', count: 8 },
        { type: 'validity', days: null, months: 1, notAfter: null },
        { type: 'start', rule: 'on_first_visit', date: null },
        { type: 'trainer' },
        { type: 'requiresPass' },
        { type: 'discounts', allowed: false },
      ])
    case 'untilYearEnd':
      return base(tp.untilYearEnd.name, '1100', [
        { type: 'validity', days: 30, notAfter: `${today.getFullYear()}-12-31` },
        { type: 'start', rule: 'on_first_visit', date: null },
      ])
  }
}

export const presetSummary = (key: PresetKey): string => summarize(toRules(presetDraft(key, null).blocks))

// ---- the tester ----

/// The zones a plan mentions, for the tester's picker — and the known ones of
/// the club (from its other plans) for the zones editor's suggestions.
export const zonesOf = (rules: PlanRules): readonly string[] => [
  ...new Set(
    rules.blocks.flatMap((block): string[] =>
      block.type === 'zones'
        ? [...block.zones]
        : block.type === 'group'
          ? block.blocks.flatMap((child) => (child.type === 'zones' ? [...child.zones] : []))
          : [],
    ),
  ),
]

/// The groups that carry their own pack — the tester asks how many are left.
export const packedGroups = (rules: PlanRules): readonly GroupBlock[] =>
  rules.blocks.filter(
    (block): block is GroupBlock => block.type === 'group' && block.blocks.some((child) => child.type === 'visits'),
  )

export const hasSharedPack = (rules: PlanRules): boolean => rules.blocks.some((block) => block.type === 'visits')

const offsetMinutes = (instant: number, timeZone: string): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(instant))
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0)
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'))
  return Math.round((asUtc - instant) / 60_000)
}

/// Wall time in a club's zone → the instant, as ISO. «Понеділок 22:00 у
/// Києві» is what the person picks; the engine wants an instant.
export const zonedInstant = (date: string, time: string, timeZone: string): string => {
  const [year = 1970, month = 1, day = 1] = date.split('-').map(Number)
  const [hour = 0, minute = 0] = time.split(':').map(Number)
  const wall = Date.UTC(year, month - 1, day, hour, minute)
  const first = wall - offsetMinutes(wall, timeZone) * 60_000
  // Once more across a clock change: the offset at the answer, not the guess.
  const second = wall - offsetMinutes(first, timeZone) * 60_000
  return new Date(second).toISOString()
}

export const todayIn = (timeZone: string, now = new Date()): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now)

export const groupLabel = (rules: PlanRules, groupId: string | null | undefined): string | null =>
  groupId
    ? (rules.blocks.find((block): block is GroupBlock => block.type === 'group' && block.id === groupId)?.label ??
      null)
    : null

